import { useRef, useState } from 'react'
import useMediaQuery from 'hooks/use-media-query'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { credencialesImage } from '../constants'

const SLIDES = [1, 2, 3]
const SWIPE_THRESHOLD = 50

// Photo carousel (Figma desktop 5694:1261, mobile 5864:3811): no autoplay,
// no arrows. The track translates with CSS variables (slide width + gap per
// breakpoint) so the active slide stays centered and neighbours peek on
// desktop; dots and swipe/drag change the slide.
const GallerySection = () => {
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const [index, setIndex] = useState(0)
  const [dragX, setDragX] = useState(0)
  const dragStart = useRef<number | null>(null)

  const goTo = (next: number) => {
    const clamped = Math.max(0, Math.min(SLIDES.length - 1, next))
    if (clamped === index) return
    dlTrackEvent('user_interaction', {
      action: 'select',
      element: `galeria :: slide :: ${clamped + 1}`,
      section: 'credenciales'
    })
    setIndex(clamped)
  }

  const onPointerDown = (event: React.PointerEvent) => {
    dragStart.current = event.clientX
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent) => {
    if (dragStart.current === null) return
    setDragX(event.clientX - dragStart.current)
  }

  const onPointerUp = () => {
    if (dragStart.current === null) return
    const delta = dragX
    dragStart.current = null
    setDragX(0)
    if (delta <= -SWIPE_THRESHOLD) goTo(index + 1)
    else if (delta >= SWIPE_THRESHOLD) goTo(index - 1)
  }

  return (
    <section className="credenciales__gallery" aria-roledescription="carousel">
      <div
        className="credenciales__gallery-viewport"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          className={`credenciales__gallery-track${dragStart.current !== null ? ' credenciales__gallery-track--dragging' : ''}`}
          style={{ ['--cred-index' as string]: index, ['--cred-drag' as string]: `${dragX}px` }}
        >
          {SLIDES.map((slide) => (
            <figure
              key={slide}
              className={`credenciales__gallery-slide${slide - 1 === index ? ' credenciales__gallery-slide--active' : ''}`}
              aria-hidden={slide - 1 !== index}
            >
              <img
                src={credencialesImage(isDesktop ? 'DESKTOP' : 'MOBILE', `img_galeria_${isDesktop ? 'desk' : 'mob'}_${slide}_2x`)}
                alt={`Momento Heineken ${slide}`}
                draggable={false}
                loading="lazy"
              />
            </figure>
          ))}
        </div>
      </div>
      <div className="credenciales__gallery-dots" role="tablist" aria-label="Fotos">
        {SLIDES.map((slide) => (
          <button
            key={slide}
            type="button"
            role="tab"
            aria-selected={slide - 1 === index}
            aria-label={`Foto ${slide}`}
            className={`credenciales__gallery-dot${slide - 1 === index ? ' credenciales__gallery-dot--active' : ''}`}
            onClick={() => goTo(slide - 1)}
          />
        ))}
      </div>
    </section>
  )
}

export default GallerySection
